"use client";

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

// ============================================================================
// Toast Notification System
// ============================================================================
// SSR-safe toast notifications for the web app.
// 
// Usage:
//   import { useToast } from "@/components/feedback/ToastProvider";
//   const toast = useToast();
//   toast.success("Operation completed!");
//   toast.error("Something went wrong");
//   toast.info("Just so you know...");
// ============================================================================

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

// Also export a direct notify helper for convenience
export const notify = {
  success: (message: string) => {
    // This will be populated by the provider
    console.warn("notify called outside ToastProvider - message:", message);
  },
  error: (message: string) => {
    console.warn("notify called outside ToastProvider - message:", message);
  },
  info: (message: string) => {
    console.warn("notify called outside ToastProvider - message:", message);
  },
  warning: (message: string) => {
    console.warn("notify called outside ToastProvider - message:", message);
  },
};

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, message: string, duration = DEFAULT_DURATION) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newToast: Toast = { id, type, message, duration };
    
    setToasts((prev) => [...prev, newToast]);
    
    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const contextValue: ToastContextValue = {
    success: useCallback((message: string, duration?: number) => addToast("success", message, duration), [addToast]),
    error: useCallback((message: string, duration?: number) => addToast("error", message, duration), [addToast]),
    info: useCallback((message: string, duration?: number) => addToast("info", message, duration), [addToast]),
    warning: useCallback((message: string, duration?: number) => addToast("warning", message, duration), [addToast]),
  };

  // Update global notify object
  notify.success = contextValue.success;
  notify.error = contextValue.error;
  notify.info = contextValue.info;
  notify.warning = contextValue.warning;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

// Toast container component
function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  );
}

// Individual toast item
function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const typeStyles: Record<ToastType, string> = {
    success: "bg-green-600 text-white",
    error: "bg-red-600 text-white",
    info: "bg-blue-600 text-white",
    warning: "bg-yellow-500 text-black",
  };

  const icons: Record<ToastType, string> = {
    success: "✓",
    error: "✕",
    info: "ℹ",
    warning: "⚠",
  };

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg ${typeStyles[toast.type]} animate-slide-in-right`}
      role="alert"
    >
      <span className="text-lg font-bold flex-shrink-0">{icons[toast.type]}</span>
      <p className="text-sm flex-1 break-words whitespace-pre-wrap">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
